import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ApiV2ReservationstatusElFindParams } from '@/apis/swaggers/v2/v2-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { MaybeGetter, QueryComposableParams, QueryOptions } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ApiV2ReservationstatusElFindParams, unknown>

const fetchReservationStatusList = async (params: ApiV2ReservationstatusElFindParams) => {
  const { data } = await apiClient.v2.apiV2ReservationstatusElFind(params);
  return checkApiResponse(data);
}

export const v2ReservationStatusKeys = {
  all: [QUERY_KEY.v2apiV2ReservationstatusElFind] as const,
  list: (params: ApiV2ReservationstatusElFindParams) => [...v2ReservationStatusKeys.all, params] as const,
}

/** 명령형(await) 호출용 쿼리 정의 — `queryClient.fetchQuery(v2ReservationStatusFindQueryOptions(params))` */
export const v2ReservationStatusFindQueryOptions = (params: ApiV2ReservationstatusElFindParams) => ({
  queryKey: v2ReservationStatusKeys.list(params),
  queryFn: () => fetchReservationStatusList(params),
})

export const useV2ReservationStatusFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: v2ReservationStatusKeys.list(resolveMaybeGetter(params)),
    queryFn: () => fetchReservationStatusList(resolveMaybeGetter(params)),
    ...resolveMaybeGetter(options ?? {}),
  }))
}

/**
 * 예약상태 목록 표준 조회 — FCM 은 sort 변경만 커버라 staleTime 5분.
 * 특수 조건 조회(기간 필터·keyword 등)는 기존 v2ReservationStatusFindQueryOptions 를 그대로 사용한다 (staleTime 0 유지).
 */
export const v2ReservationStatusListQueryOptions = (hospitalID: string) => ({
  // swagger 생성 타입이 hospitalID 를 boolean 으로 잘못 문서화하고 있어 캐스팅 (서버 스웨거 주석 오류)
  ...v2ReservationStatusFindQueryOptions({ hospitalID, isRemoved: false, key: 'sortNum', way: 1 } as unknown as ApiV2ReservationstatusElFindParams),
  staleTime: 5 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
})

/**
 * 표준 목록 조회의 선언형 구독 훅 — 표준 파라미터·staleTime 은 v2ReservationStatusListQueryOptions 이 관리하고,
 * enabled 등 컴포넌트별 옵션은 사용처(options)에서 제어한다.
 */
export const useV2ReservationStatusListQuery = ({ hospitalID, options }: {
  hospitalID: MaybeGetter<string | undefined>
  options?: QueryOptions
}) => {
  return useQuery(() => {
    const id = resolveMaybeGetter(hospitalID)
    return {
      ...v2ReservationStatusListQueryOptions(id as string),
      enabled: !!id,
      ...resolveMaybeGetter(options ?? {}),
    }
  })
}
